import React, { useState, useRef } from "react";
import {
    Button,
    Card,
    Col,
    InputNumber,
    Row,
    Select,
    Space,
    Switch,
    Radio,
    Tooltip,
    Typography,
    Divider,
} from "antd";
import { CopyOutlined, CheckOutlined, UploadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Document, Page } from "react-pdf";

const { Title, Text } = Typography;

const VIEWER_WIDTH = 680;

export function ConfigurarPosicionesPdfPage() {
    const [pdfUrl, setPdfUrl] = useState(null);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pdfPageDims, setPdfPageDims] = useState({ width: 0, height: 0 });

    const [mode, setMode] = useState("busqueda");
    const [drawing, setDrawing] = useState(false);
    const [rect, setRect] = useState(null);
    const [resultado, setResultado] = useState(null);
    const [copied, setCopied] = useState(false);

    // Firma config
    const [firmaPorPagina, setFirmaPorPagina] = useState(false);
    const [letraNegrita, setLetraNegrita] = useState(false);

    const overlayRef = useRef(null);
    const startPos = useRef({ x: 0, y: 0 });
    const isDrawing = useRef(false);

    const pdfScale = pdfPageDims.width > 0 ? VIEWER_WIDTH / pdfPageDims.width : 1;

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        setPdfUrl(URL.createObjectURL(file));
        setCurrentPage(1);
        setRect(null);
        setResultado(null);
    };

    const onDocumentLoadSuccess = ({ numPages }) => {
        setTotalPages(numPages);
    };

    const onPageLoadSuccess = (page) => {
        // page.view = [llX, llY, urX, urY] in PDF points
        setPdfPageDims({ width: page.view[2], height: page.view[3] });
        setRect(null);
        setResultado(null);
    };

    const getRelativePos = (e) => {
        const bounds = overlayRef.current.getBoundingClientRect();
        return { x: e.clientX - bounds.left, y: e.clientY - bounds.top };
    };

    const handleMouseDown = (e) => {
        e.preventDefault();
        const pos = getRelativePos(e);
        startPos.current = pos;
        isDrawing.current = true;
        setDrawing(true);
        setRect({ startX: pos.x, startY: pos.y, endX: pos.x, endY: pos.y });
    };

    const handleMouseMove = (e) => {
        if (!isDrawing.current) return;
        const pos = getRelativePos(e);
        setRect((prev) => ({ ...prev, endX: pos.x, endY: pos.y }));
    };

    const handleMouseUp = (e) => {
        if (!isDrawing.current) return;
        isDrawing.current = false;
        setDrawing(false);
        const pos = getRelativePos(e);
        const normalized = {
            startX: Math.min(startPos.current.x, pos.x),
            startY: Math.min(startPos.current.y, pos.y),
            endX: Math.max(startPos.current.x, pos.x),
            endY: Math.max(startPos.current.y, pos.y),
        };
        setRect(normalized);
        computeResultado(normalized, mode, firmaPorPagina, letraNegrita, currentPage);
    };

    const computeResultado = (r, currentMode, porPagina, negrita, pagina) => {
        const round1 = (v) => Math.round(v * 10) / 10;
        // Convert browser top-left origin → PDF bottom-left origin
        const llX = round1(r.startX / pdfScale);
        const urX = round1(r.endX / pdfScale);
        const llY = round1(pdfPageDims.height - r.endY / pdfScale);
        const urY = round1(pdfPageDims.height - r.startY / pdfScale);

        if (currentMode === "busqueda") {
            setResultado({ busquedaRangoLlX: llX, busquedaRangoLlY: llY, busquedaRangoUrX: urX, busquedaRangoUrY: urY });
        } else {
            setResultado({
                ancho: Math.round(urX - llX),
                alto: Math.round(urY - llY),
                posicionX: llX,
                posicionY: llY,
                firmaPorPagina: porPagina,
                pagina,
                letraNegrita: negrita,
            });
        }
    };

    const handleModeChange = (e) => {
        setMode(e.target.value);
        setRect(null);
        setResultado(null);
    };

    const handlePageChange = (p) => {
        setCurrentPage(p);
        setRect(null);
        setResultado(null);
    };

    const handleFirmaSwitchChange = (field, value) => {
        const porPagina = field === "firmaPorPagina" ? value : firmaPorPagina;
        const negrita = field === "letraNegrita" ? value : letraNegrita;
        if (field === "firmaPorPagina") setFirmaPorPagina(value);
        else setLetraNegrita(value);
        if (rect && mode === "firma") {
            computeResultado(rect, "firma", porPagina, negrita, currentPage);
        }
    };

    const rectStyle = rect
        ? {
              position: "absolute",
              left: Math.min(rect.startX, rect.endX),
              top: Math.min(rect.startY, rect.endY),
              width: Math.abs(rect.endX - rect.startX),
              height: Math.abs(rect.endY - rect.startY),
              border: `2px solid ${mode === "busqueda" ? "#1890ff" : "#52c41a"}`,
              backgroundColor: mode === "busqueda" ? "rgba(24,144,255,0.12)" : "rgba(82,196,26,0.12)",
              pointerEvents: "none",
          }
        : {};

    const jsonStr = resultado ? JSON.stringify(resultado, null, 2) : null;

    const handleCopy = () => {
        if (!jsonStr) return;
        navigator.clipboard.writeText(jsonStr).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div style={{ padding: "24px" }}>
            <Title level={4} style={{ marginBottom: 16 }}>
                Configurar Posiciones PDF
            </Title>

            {/* Toolbar */}
            <Card size="small" style={{ marginBottom: 16 }}>
                <Space wrap size="middle">
                    <Button
                        icon={<UploadOutlined />}
                        onClick={() => document.getElementById("pdf-upload-input").click()}
                    >
                        Cargar PDF
                    </Button>
                    <input
                        id="pdf-upload-input"
                        type="file"
                        accept=".pdf"
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                    />

                    {pdfUrl && (
                        <>
                            <Divider type="vertical" />

                            <Radio.Group value={mode} onChange={handleModeChange} buttonStyle="solid">
                                <Radio.Button value="busqueda">Rango de Búsqueda</Radio.Button>
                                <Radio.Button value="firma">Posición de Firma</Radio.Button>
                            </Radio.Group>

                            {totalPages > 1 && (
                                <Space size="small">
                                    <Text>Página:</Text>
                                    <Select
                                        value={currentPage}
                                        onChange={handlePageChange}
                                        style={{ width: 72 }}
                                        size="small"
                                    >
                                        {Array.from({ length: totalPages }, (_, i) => (
                                            <Select.Option key={i + 1} value={i + 1}>
                                                {i + 1}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                    <Text type="secondary">/ {totalPages}</Text>
                                </Space>
                            )}

                            {mode === "firma" && (
                                <>
                                    <Divider type="vertical" />
                                    <Space size="small">
                                        <Text style={{ fontSize: 13 }}>Por página:</Text>
                                        <Switch
                                            size="small"
                                            checked={firmaPorPagina}
                                            onChange={(v) => handleFirmaSwitchChange("firmaPorPagina", v)}
                                        />
                                    </Space>
                                    <Space size="small">
                                        <Text style={{ fontSize: 13 }}>Negrita:</Text>
                                        <Switch
                                            size="small"
                                            checked={letraNegrita}
                                            onChange={(v) => handleFirmaSwitchChange("letraNegrita", v)}
                                        />
                                    </Space>
                                </>
                            )}
                        </>
                    )}
                </Space>
            </Card>

            {!pdfUrl && (
                <Card style={{ textAlign: "center", padding: "48px 0" }}>
                    <Space direction="vertical">
                        <UploadOutlined style={{ fontSize: 40, color: "#bfbfbf" }} />
                        <Text type="secondary">Carga un PDF para comenzar a configurar posiciones</Text>
                        <Button
                            type="primary"
                            icon={<UploadOutlined />}
                            onClick={() => document.getElementById("pdf-upload-input").click()}
                        >
                            Seleccionar PDF
                        </Button>
                    </Space>
                </Card>
            )}

            {pdfUrl && (
                <Row gutter={16}>
                    {/* PDF Viewer */}
                    <Col xs={24} lg={16}>
                        <Card
                            size="small"
                            title={
                                <Space size={4}>
                                    <InfoCircleOutlined style={{ color: mode === "busqueda" ? "#1890ff" : "#52c41a" }} />
                                    <Text style={{ fontSize: 13 }}>
                                        {mode === "busqueda"
                                            ? "Dibuja un rectángulo sobre el área donde se buscará el identificador (DNI, nombre, etc.)"
                                            : "Dibuja un rectángulo sobre el área donde irá la firma del colaborador"}
                                    </Text>
                                </Space>
                            }
                            bodyStyle={{ padding: 0, overflow: "auto", maxHeight: "78vh" }}
                        >
                            <div style={{ position: "relative", display: "inline-block", lineHeight: 0 }}>
                                <Document
                                    file={pdfUrl}
                                    onLoadSuccess={onDocumentLoadSuccess}
                                    loading={<div style={{ padding: 40, textAlign: "center" }}>Cargando PDF...</div>}
                                >
                                    <Page
                                        pageNumber={currentPage}
                                        width={VIEWER_WIDTH}
                                        onLoadSuccess={onPageLoadSuccess}
                                        loading={null}
                                        renderAnnotationLayer={false}
                                        renderTextLayer={false}
                                    />
                                </Document>

                                {/* Interaction overlay */}
                                <div
                                    ref={overlayRef}
                                    style={{ position: "absolute", inset: 0, cursor: "crosshair", userSelect: "none" }}
                                    onMouseDown={handleMouseDown}
                                    onMouseMove={handleMouseMove}
                                    onMouseUp={handleMouseUp}
                                    onMouseLeave={() => {
                                        if (isDrawing.current) {
                                            isDrawing.current = false;
                                            setDrawing(false);
                                        }
                                    }}
                                >
                                    {rect && <div style={rectStyle} />}
                                </div>
                            </div>
                        </Card>
                    </Col>

                    {/* Output panel */}
                    <Col xs={24} lg={8}>
                        <Card
                            size="small"
                            title="Coordenadas"
                            extra={
                                jsonStr && (
                                    <Tooltip title={copied ? "¡Copiado!" : "Copiar JSON"}>
                                        <Button
                                            size="small"
                                            icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                                            type={copied ? "primary" : "default"}
                                            onClick={handleCopy}
                                        />
                                    </Tooltip>
                                )
                            }
                            style={{ marginBottom: 12 }}
                        >
                            {jsonStr ? (
                                <pre
                                    style={{
                                        background: "#fafafa",
                                        border: "1px solid #f0f0f0",
                                        borderRadius: 6,
                                        padding: "12px 14px",
                                        fontSize: 13,
                                        lineHeight: 1.6,
                                        margin: 0,
                                        overflow: "auto",
                                        maxHeight: "50vh",
                                    }}
                                >
                                    {jsonStr}
                                </pre>
                            ) : (
                                <div style={{ padding: "20px 0", textAlign: "center" }}>
                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                        Dibuja un rectángulo sobre el PDF para ver las coordenadas
                                    </Text>
                                </div>
                            )}
                        </Card>

                        <Card size="small" title="Referencia de campos">
                            {mode === "busqueda" ? (
                                <Space direction="vertical" size={2} style={{ fontSize: 12, width: "100%" }}>
                                    <Text type="secondary">
                                        Define el rectángulo de búsqueda donde se localizará el texto identificador dentro del PDF (p. ej., DNI del colaborador).
                                    </Text>
                                    <Divider style={{ margin: "8px 0" }} />
                                    <Text><b>LlX / LlY</b> — esquina inferior izquierda</Text>
                                    <Text><b>UrX / UrY</b> — esquina superior derecha</Text>
                                    <Text type="secondary" style={{ marginTop: 4 }}>
                                        Coordenadas en puntos PDF (origen: esquina inferior izquierda de la página).
                                    </Text>
                                </Space>
                            ) : (
                                <Space direction="vertical" size={2} style={{ fontSize: 12, width: "100%" }}>
                                    <Text type="secondary">
                                        Define el rectángulo donde se colocará la firma digital del colaborador.
                                    </Text>
                                    <Divider style={{ margin: "8px 0" }} />
                                    <Text><b>posicionX / posicionY</b> — esquina inferior izquierda</Text>
                                    <Text><b>ancho / alto</b> — dimensiones en puntos PDF</Text>
                                    <Text><b>pagina</b> — número de página actual</Text>
                                    <Text><b>firmaPorPagina</b> — aplicar en todas las páginas</Text>
                                    <Text><b>letraNegrita</b> — texto de la firma en negrita</Text>
                                </Space>
                            )}
                        </Card>
                    </Col>
                </Row>
            )}
        </div>
    );
}
